// diagramFromSupabaseSchema — Admin-only ERD-generator. Läser
// information_schema via `introspect_db_schema`-RPC:n (AA2a) och bygger
// ett multi-sheet-diagram (en sheet per Postgres-schema, ett block per
// tabell, FK:er som connectors inom schema + cross-sheet-länkar för
// FKs mellan adjacent schemas).
//
// AB1: ENBART admin. DB-schema får aldrig exponeras för end-users —
// varken struktur, kolumnnamn eller RLS-policies. Tre lager skydd:
//   1. Skill.requiresAdmin = true → UI-filter döljer skillen.
//   2. Plannern returnerar error-step om !ctx.isAdmin (utan att röra
//      schema-datan).
//   3. RPC:n `introspect_db_schema` har inbyggd `has_sysadmin`-check
//      som defense-in-depth på server-sidan.

import { gridPosition } from '@/lib/aiDiagramTools';
import {
  registerSkill,
  type Skill,
  type SkillContext,
  type ExecutionPlan,
  type SkillStep,
} from '@/lib/aiSkills/skillRegistry';

export interface SchemaColumn {
  name: string;
  type: string;
  nullable: boolean;
  isPrimary?: boolean;
}

export interface SchemaTable {
  name: string;
  schema: string;
  columns: SchemaColumn[];
}

export interface SchemaFK {
  fromTable: string;
  fromColumn: string;
  toTable: string;
  toColumn: string;
  /** AA2: optional schema-qualifier för cross-schema FK-detektion. */
  fromSchema?: string;
  toSchema?: string;
}

export interface DbSchema {
  tables: SchemaTable[];
  foreignKeys: SchemaFK[];
}

export interface SchemaSkillContext extends SkillContext {
  /** Färdig schema-snapshot. supabaseSchemaLoader populerar via RPC. */
  schema?: DbSchema;
  /** AB1: admin-flag, samma gating som diagramFromCodebase. */
  isAdmin?: boolean;
  /** Subset av schemas att inkludera i plan:en. Default alla från schemat. */
  includeSchemas?: string[];
  /** Max tables per sheet innan trunkering. Default 15. */
  maxTablesPerSheet?: number;
}

const SCHEMA_COLORS = [
  '#3ecf8e', '#4f46e5', '#f97316', '#dc2626', '#8b5cf6',
  '#06b6d4', '#eab308', '#ec4899', '#22c55e', '#1a2e5a',
];

/** Kort summering av kolumner för block.description. */
function summariseColumns(columns: SchemaColumn[]): string {
  if (columns.length === 0) return 'Inga kolumner';
  const pks = columns.filter((c) => c.isPrimary).map((c) => `PK:${c.name}`);
  const sample = columns.slice(0, 5).map((c) => `${c.name}: ${c.type}`);
  const more = columns.length > 5 ? ` +${columns.length - 5} till` : '';
  return [...pks, ...sample].join('\n') + more;
}

/** Gruppera tabeller per schema, alfabetiskt sorterade. */
function groupTablesBySchema(schema: DbSchema, includeSchemas?: string[]): Map<string, SchemaTable[]> {
  const map = new Map<string, SchemaTable[]>();
  for (const t of schema.tables) {
    if (includeSchemas && !includeSchemas.includes(t.schema)) continue;
    if (!map.has(t.schema)) map.set(t.schema, []);
    map.get(t.schema)!.push(t);
  }
  // Sortera tabeller alfabetiskt inom schema.
  for (const [, tables] of map) {
    tables.sort((a, b) => a.name.localeCompare(b.name));
  }
  return map;
}

const TRIGGER_PATTERNS = [
  /\b(?:ERD|entity(?:-| )relation(?:ship)?)\b/i,
  /schema(?:-| )?diagram/i,
  /databas(?:-| )?(?:schema|karta)/i,
  /rita\s+(?:db|databasen|schemat)/i,
];

function matchesSchemaIntent(prompt: string): boolean {
  return TRIGGER_PATTERNS.some((re) => re.test(prompt));
}

/**
 * Planera ERD från schema-snapshot. En sheet per Postgres-schema, ett
 * block per tabell med kolumn-summary, FK:er som connectors. Cross-sheet-
 * länkar för FKs mellan adjacent schemas (|from-to|===1 som invariant).
 *
 * Tre tidiga exits (defense-in-depth):
 *   1. !isAdmin → error-step, DB-data rörs aldrig
 *   2. !schema → "loader-ej-körd"-step (runtime-glue missing)
 *   3. schema.tables.length=0 → "empty-schema"-step
 */
export function planDiagramFromSupabaseSchema(ctx: SchemaSkillContext): ExecutionPlan {
  if (!ctx.isAdmin) {
    return makeErrorPlan(ctx.userPrompt, 'Admin-behörighet krävs — denna skill läser DB-schema.');
  }
  if (!ctx.schema) {
    return makeErrorPlan(ctx.userPrompt, 'Schema-loader har inte körts — anropa loadSupabaseSchema() först.');
  }
  const grouped = groupTablesBySchema(ctx.schema, ctx.includeSchemas);
  if (grouped.size === 0) {
    return makeErrorPlan(ctx.userPrompt, 'Inga tabeller i valt/e schema.');
  }

  const maxTables = ctx.maxTablesPerSheet ?? 15;
  const steps: SkillStep[] = [];
  let idx = 0;
  const sheetRefForSchema = (s: string) => `sheet-${s}`;

  steps.push({
    index: idx++,
    tool: 'plan_diagram',
    args: {
      goal: ctx.userPrompt,
      sheet_count: grouped.size,
      style: 'architecture',
    },
    description: `Planera ${grouped.size} schemas, ${ctx.schema.tables.length} tabeller totalt`,
  });

  // Alfabetisk schema-ordning ger stabil sheet-indexering → adjacency-
  // check för cross-sheet FK:er blir deterministisk.
  const schemaOrder = Array.from(grouped.keys()).sort();
  const schemaIndex = new Map<string, number>();
  schemaOrder.forEach((s, i) => schemaIndex.set(s, i));

  // Globalt map:ar fullt kvalificerade tabell-refs → block-refs så
  // cross-sheet-FK:er kan resolver:a över sheets.
  const blockRefByTable = new Map<string, string>();

  for (let sIdx = 0; sIdx < schemaOrder.length; sIdx++) {
    const schemaName = schemaOrder[sIdx];
    const tables = grouped.get(schemaName)!;
    const sheetRef = sheetRefForSchema(schemaName);
    const color = SCHEMA_COLORS[sIdx % SCHEMA_COLORS.length];

    steps.push({
      index: idx++,
      tool: 'create_sheet',
      args: {
        sheet_ref: sheetRef,
        name: `${ordinal(sIdx)} ${schemaName}`,
        color,
        description: `${tables.length} tabeller i schema "${schemaName}"`,
        icon: 'Database',
      },
      description: `Skapa schema-sheet "${schemaName}" (${tables.length} tabeller)`,
    });

    // Trunkera stora schemas så inte en sheet får 100+ blocks.
    const truncated = tables.slice(0, maxTables);
    const blocks = truncated.map((t, i) => {
      const pos = gridPosition(i);
      // Spara FULL-qualified lookup så cross-schema FK:er kan hittas.
      const tableRef = `${sheetRef}-${t.name}`;
      blockRefByTable.set(`${t.schema}.${t.name}`, tableRef);
      return {
        ref: tableRef,
        type: t.columns.some((c) => c.isPrimary) ? 'general' : 'condition',
        x: pos.x,
        y: pos.y,
        config: {
          name: t.name,
          description: summariseColumns(t.columns),
          icon: 'Table',
        },
        color,
      };
    });
    steps.push({
      index: idx++,
      tool: 'batch_create_blocks',
      args: { sheet_ref: sheetRef, blocks },
      description: `${blocks.length} tabell-blocks${tables.length > maxTables ? ` (truncerat från ${tables.length})` : ''}`,
    });
  }

  // Inom-schema FK:er → batch_create_connectors per sheet.
  // Cross-schema FK:er (adjacent) → create_cross_sheet_link.
  // Cross-schema FK:er (non-adjacent) → skippas med log (regel från workflow.ts).
  const withinSchemaFks = new Map<string, Array<{ fromRef: string; toRef: string; label?: string }>>();
  const crossSchemaFks: Array<{
    fromIdx: number; toIdx: number;
    fromRef: string; toRef: string; label: string;
  }> = [];

  for (const fk of ctx.schema.foreignKeys) {
    const fromSchema = fk.fromSchema ?? 'public';
    const toSchema = fk.toSchema ?? 'public';
    const fromKey = `${fromSchema}.${fk.fromTable}`;
    const toKey = `${toSchema}.${fk.toTable}`;
    const fromRef = blockRefByTable.get(fromKey);
    const toRef = blockRefByTable.get(toKey);
    if (!fromRef || !toRef) continue; // target truncerad eller inte inkluderad

    const label = `${fk.fromColumn}→${fk.toColumn}`;
    if (fromSchema === toSchema) {
      const sheetRef = sheetRefForSchema(fromSchema);
      if (!withinSchemaFks.has(sheetRef)) withinSchemaFks.set(sheetRef, []);
      withinSchemaFks.get(sheetRef)!.push({ fromRef, toRef, label });
    } else {
      const fromIdx = schemaIndex.get(fromSchema);
      const toIdx = schemaIndex.get(toSchema);
      if (fromIdx === undefined || toIdx === undefined) continue;
      if (Math.abs(fromIdx - toIdx) !== 1) continue; // non-adjacent skipped
      crossSchemaFks.push({ fromIdx, toIdx, fromRef, toRef, label });
    }
  }

  for (const [sheetRef, edges] of withinSchemaFks) {
    if (edges.length === 0) continue;
    steps.push({
      index: idx++,
      tool: 'batch_create_connectors',
      args: { sheet_ref: sheetRef, edges },
      description: `${edges.length} foreign keys inom ${sheetRef}`,
    });
  }

  for (const cs of crossSchemaFks) {
    steps.push({
      index: idx++,
      tool: 'create_cross_sheet_link',
      args: {
        from_sheet_index: cs.fromIdx,
        to_sheet_index: cs.toIdx,
        from_block_ref: cs.fromRef,
        to_block_ref: cs.toRef,
        label: cs.label,
      },
      description: `Cross-schema FK: ${schemaOrder[cs.fromIdx]} → ${schemaOrder[cs.toIdx]}`,
    });
  }

  steps.push({
    index: idx++,
    tool: 'validate_diagram',
    args: {},
    description: 'Verifiera ERD-invariants',
  });

  const totalTables = Array.from(grouped.values()).reduce(
    (n, tables) => n + Math.min(tables.length, maxTables),
    0,
  );
  const totalFks = ctx.schema.foreignKeys.length;
  return {
    skill: 'diagramFromSupabaseSchema',
    goal: ctx.userPrompt,
    steps,
    estimatedCost: 100 + totalTables * 4 + totalFks * 2 + grouped.size * 30,
  };
}

function makeErrorPlan(goal: string, message: string): ExecutionPlan {
  return {
    skill: 'diagramFromSupabaseSchema',
    goal,
    steps: [{
      index: 0,
      tool: 'plan_diagram',
      args: { goal },
      description: message,
    }],
    estimatedCost: 0,
  };
}

function ordinal(i: number): string {
  return ['①', '②', '③', '④', '⑤', '⑥', '⑦', '⑧', '⑨', '⑩'][i] ?? `(${i + 1})`;
}

const skill: Skill = {
  name: 'diagramFromSupabaseSchema',
  description:
    'Bygg en ERD från Supabase-schemat — en sheet per schema, ett block per tabell, foreign keys som connectors.',
  trigger: matchesSchemaIntent,
  // AB1: admin-only. DB-schema är privilegierad info; end-users får
  // aldrig se tabellnamn/kolumnnamn/RLS via AI-kanalen.
  requiresAdmin: true,
  plan: (ctx) => planDiagramFromSupabaseSchema(ctx as SchemaSkillContext),
};

registerSkill(skill);

export default skill;
